import ResizableContainer from "../core/ResizableContainer";
import ResizableScene from "../core/ResizableScene";
import { UserMediaContainer } from "../core/UserMediaContainer";

export default class Photo extends ResizableContainer {
	public static readonly ACTIVE_ALPHA = { min: 0.6, max: 1 };
	public static readonly ACTIVE_SCALE = { min: 0.5, max: 0.5 };

	private _userMediaContainer?:UserMediaContainer;
	public get mediaStreamStatus(): "loading" | "success" | "failed" | "stopped" {
		if (this._userMediaContainer) {
			return this._userMediaContainer.status;
		}
		return "success";
	};

	protected photoBase: Phaser.GameObjects.Image;
	protected photoShadow: Phaser.GameObjects.Image;

	picture: Phaser.GameObjects.Image;
	protected decorationContainer?: Phaser.GameObjects.Container;
	protected stickerContainer?: Phaser.GameObjects.Container;
	sizer: Phaser.GameObjects.Image;

	protected photoCorners: Phaser.GameObjects.Image;
	protected addedFrame?: Phaser.GameObjects.Image;

	protected filter?: Phaser.GameObjects.Image;
	protected maskImg?: Phaser.GameObjects.Image;
	photoMask: Phaser.Display.Masks.BitmapMask;
	maskedContainer:Phaser.GameObjects.Container;

	hasAddedFrame:boolean;
	protected _photo: string;
	public get photoKey(): string {
		return this._photo;
	}

	constructor(scene: ResizableScene, photo: string, showFrame: boolean, useMask: boolean) {
		super(scene, 0, 0);
		this._photo = photo;

		const photoBase = this.photoBase = scene.add.image(0, 0, "takePhotoBase");
		const photoShadow = this.photoShadow = scene.add.image(0, 0, "takePhotoShadow");
		this.add([photoBase, photoShadow]);

		this.maskedContainer = scene.add.container();
		this.add(this.maskedContainer);

		const picture = this.picture = scene.add.image(0, 0, photo);
		const sizer = this.sizer = scene.add.image(0, 0, "photoFrame");
		this.maskedContainer.setSize(sizer.width, sizer.height);
		sizer.setVisible(false);
		this.maskedContainer.add([picture, sizer]);
		this.setSize(sizer.width, sizer.height);

		this.decorationContainer = scene.add.container();
		this.maskedContainer.add(this.decorationContainer);

		this.filter = scene.add.image(0, 0, "whiteSquare");
		this.filter.setVisible(false);
		this.maskedContainer.add(this.filter);

		this.stickerContainer = scene.add.container();
		this.maskedContainer.add(this.stickerContainer);

		if (useMask) {
			const maskImg = this.maskImg = scene.add.image(0, 0, "takePhotoMask");
			const mask = this.photoMask = maskImg.createBitmapMask();
			maskImg.once(Phaser.GameObjects.Events.DESTROY, mask.destroy, mask);
			maskImg.setVisible(false);

			// TODO: mask currently darkens elements within the containers, noticable on elements with more transperancy (glasses)
			this.maskedContainer.setMask(mask);
			this.addResize(() => {
				this.updateMaskPosition();
			});
		}

		const photoCorners = this.photoCorners = scene.add.image(0, 0, "takePhotoCorners");
		this.add(photoCorners);

		this.addedFrame = scene.add.image(0, 0, "whiteSquare");
		this.addedFrame.scale = 1 / 1.1;
		this.addedFrame.setVisible(false);
		this.add(this.addedFrame);

		this.setPhoto(photo);

		if (!showFrame) {
			photoBase.visible = false;
			photoShadow.visible = false;
			photoCorners.visible = false;
		}
	}

	updateMaskPosition():void {
		if (this.maskImg) {
			const scaleTransform = this.getWorldTransformMatrix();
			const decoTransform = this.decorationContainer.getWorldTransformMatrix();
			const size = new Phaser.Math.Vector2(this.picture.displayWidth * scaleTransform.scaleX, this.picture.displayHeight * scaleTransform.scaleY);
			this.maskImg.setPosition(decoTransform.tx, decoTransform.ty);
			this.maskImg.setSize(size.x, size.y);
			this.maskImg.setDisplaySize(size.x, size.y);
			this.maskImg.setRotation(scaleTransform.rotation);
		}
	}

	setPhoto(photo: string): void {
		this._photo = photo;
		this.picture.setTexture(photo);
		const widthRatio = (this.sizer.width) / this.picture.width;
		const heightRatio = (this.sizer.height) / this.picture.height;
		const photoScale = Math.min(widthRatio, heightRatio);
		this.picture.setScale(photoScale);
		this.resize();
	}

	getMediaContainer(): UserMediaContainer {
		return this._userMediaContainer;
	}

	addDecoration(decoration: Phaser.GameObjects.Image | Phaser.GameObjects.Container, sticker = false): void {
		if (!this.decorationContainer) return;
		const decoTransform = decoration.getWorldTransformMatrix();
		const photoTransform = this.getWorldTransformMatrix();
		if (!sticker) this.decorationContainer.add(decoration);
		else this.stickerContainer.add(decoration);
		const position = photoTransform.applyInverse(decoTransform.tx, decoTransform.ty);
		decoration.setPosition(position.x, position.y);
		decoration.setScale(decoration.scale / this.scale);
	}

	removeDecoration(decoration: Phaser.GameObjects.Image | Phaser.GameObjects.Container, sticker = false): void {
		if (!this.decorationContainer) return;
		const decoTransform = decoration.getWorldTransformMatrix();
		if (!sticker) this.decorationContainer.remove(decoration);
		else this.stickerContainer.remove(decoration);
		decoration.setPosition(decoTransform.tx, decoTransform.ty);
		decoration.setScale(decoration.scale * this.scale);
	}

	setFilter(texture?: string, frame?: number | string): boolean {
		if (!this.filter) return false;
		if (texture) {
			if (this.filter.visible && this.filter.texture.key == texture) {
				this.filter.setVisible(false);
				return false;
			}
			this.filter.setTexture(texture, frame);
			this.filter.setVisible(true);

			const widthScale = this.picture.displayWidth / this.filter.width;
			const heightScale = this.picture.displayHeight / this.filter.height;
			this.filter.setScale(widthScale, heightScale);
			return true;
		} else {
			this.filter.setVisible(false);
		}
		return false;
	}

	setFrame(texture?:string, frame?: number | string): boolean {
		if (!texture || texture == "" || texture == "no-frame" || (texture && this.addedFrame.texture.key == texture)) {
			this.hasAddedFrame = false;
			this.addedFrame.setVisible(false);
			this.addedFrame.setTexture("no-frame");
			return false;
		}
		this.hasAddedFrame = true;
		this.addedFrame.setVisible(true);
		this.addedFrame.setTexture(texture, frame);
		return true;
	}

	enablePhoto(active: boolean): void {
		const alpha = Photo.ACTIVE_ALPHA;
		this.setAlpha(active ? alpha.max : alpha.min);
	}

	public isEnabled():boolean {
		return this.alpha == Photo.ACTIVE_ALPHA.max;
	}

	capture():void {
		if(this._userMediaContainer){
			const size = this._userMediaContainer.getStreamSize();
			const key = 'userVideoFeed';
			const snapshot = this._userMediaContainer.snapshot(key, size.x, size.y);
			if (snapshot != null) this._photo = key;
		}
	}

	savePhoto(key:string, onComplete:(img:HTMLImageElement)=>void): void {
		const scale = this.scale;
		this.setScale(1);

		const w1 = Math.floor(this.picture.displayWidth);
		const h1 = Math.floor(this.picture.displayHeight);
		const w2 = Math.floor(this.addedFrame.visible ? this.addedFrame.displayWidth : this.photoBase.displayWidth);
		const h2 = Math.floor(this.addedFrame.visible ? this.addedFrame.displayHeight : this.photoBase.displayHeight);

		const pictureRT = this.scene.add.renderTexture(-10000, 0, w1, h1);
		const finalRT = this.scene.add.renderTexture(-10000, 0, w2, h2);
		const picOffset = new Phaser.Math.Vector2(w1 / 2, h1 / 2);

		finalRT.draw(this.photoBase, finalRT.width / 2, finalRT.height / 2);
		finalRT.draw(this.photoShadow, finalRT.width / 2, finalRT.height / 2);

		pictureRT.draw(this.picture, picOffset.x, picOffset.y);
		if (this.decorationContainer) pictureRT.draw(this.decorationContainer, picOffset.x, picOffset.y);
		if (this.filter && this.filter.visible) pictureRT.draw(this.filter, picOffset.x, picOffset.y);
		if (this.stickerContainer) pictureRT.draw(this.stickerContainer, picOffset.x, picOffset.y);
		finalRT.draw(pictureRT, (finalRT.width - pictureRT.width) / 2, (finalRT.height - pictureRT.height) / 2);

		finalRT.draw(this.photoCorners, finalRT.width / 2, finalRT.height / 2);
		if (this.addedFrame.visible) finalRT.draw(this.addedFrame, finalRT.width / 2, finalRT.height / 2);

		this.setScale(scale);
		finalRT.snapshot((snapshot:HTMLImageElement)=>{
			this.scene.textures.addImage(key, snapshot);
			pictureRT.destroy();
			finalRT.destroy();
			onComplete(snapshot);
		});
	}

	getFilter(): string {
		if (this.filter && this.filter.visible) return this.filter.texture.key;
		return "";
	}

	getFrame(): string {
		if (this.addedFrame && this.addedFrame.visible) return this.addedFrame.texture.key;
		return "";
	}

	getWorldBounds(): Phaser.Geom.Rectangle {
		const transform = this.getWorldTransformMatrix();
		const size = new Phaser.Math.Vector2(transform.scaleX, transform.scaleY);
		size.x *= this.picture.displayWidth;
		size.y *= this.picture.displayHeight;
		return new Phaser.Geom.Rectangle(transform.tx - size.x / 2, transform.ty - size.y / 2, size.x, size.y);
	}
}